// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module CohortTable
 * @package @geenius/analytics/react
 * @description Renders the React cohort-retention table for comparing cohort
 * sizes, retention checkpoints, and last activity across segments.
 */

import type { CohortAnalysis, CohortBucket } from "@geenius/analytics";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@geenius/ui/react";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";

/**
 * Props for rendering the React cohort retention table.
 *
 * @property analysis Cohort analysis payload produced by shared helpers.
 * @property title Optional section title override.
 * @property maxRows Maximum cohort rows to render.
 * @property isLoading Whether the table should render placeholder rows.
 * @property error Optional error message or object rendered with retry recovery.
 * @property onRetry Optional retry callback for error states.
 * @property className Optional class name merged into the card wrapper.
 */
export interface CohortTableProps {
  analysis: CohortAnalysis | null;
  title?: string;
  maxRows?: number;
  isLoading?: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  className?: string;
}

function formatPercent(value: number | undefined): string {
  return `${(value ?? 0).toFixed(1)}%`;
}

function getRetentionPoint(cohort: CohortBucket, day: number) {
  return cohort.retained.find((point) => point.day === day);
}

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

/**
 * Renders a cohort retention table for the React analytics variant.
 *
 * @param props Cohort analysis data and optional loading/error controls.
 * @returns A card-framed cohort table with accessible row and column labels.
 */
export function CohortTable({
  analysis,
  className,
  error,
  isLoading,
  maxRows = 6,
  onRetry,
  title,
}: CohortTableProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const intervals = analysis?.intervals ?? [];
  const cohorts = (analysis?.cohorts ?? []).slice(0, maxRows);
  const tableTitle =
    title ?? analysis?.name ?? copy.t("analytics.dashboard.cohorts");

  return (
    <Card className={className} data-analytics-cohort-table>
      <CardHeader className="pb-0">
        <CardTitle className="text-sm" data-analytics-section-title>
          {tableTitle}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {error ? (
          <div className="grid gap-3" role="alert">
            <span className="text-sm text-muted-foreground">
              {getErrorMessage(error)}
            </span>
            {onRetry && (
              <Button
                className="w-fit"
                type="button"
                variant="secondary"
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
          </div>
        ) : isLoading ? (
          <div className="grid gap-3" data-analytics-cohort-table-loading>
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : cohorts.length === 0 ? (
          <EmptyState
            bordered={false}
            compact
            title={copy.t("analytics.dashboard.noCohorts")}
          />
        ) : (
          <div className="overflow-x-auto" data-analytics-cohort-table-viewport>
            <Table
              className="w-full"
              data-analytics-table
              aria-label={tableTitle}
            >
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">
                    {copy.t("analytics.cohorts.columns.cohort")}
                  </TableHead>
                  <TableHead scope="col">
                    {copy.t("analytics.cohorts.columns.size")}
                  </TableHead>
                  {intervals.map((day) => (
                    <TableHead key={day} scope="col">
                      {copy.t("analytics.dashboardBuilder.dayLabel", { day })}
                    </TableHead>
                  ))}
                  <TableHead scope="col">
                    {copy.t("analytics.cohorts.columns.lastActivity")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cohorts.map((cohort) => (
                  <TableRow key={cohort.cohort} data-analytics-cohort-row>
                    <th
                      className="p-2 text-left align-middle font-medium text-foreground"
                      scope="row"
                    >
                      {cohort.cohort}
                    </th>
                    <TableCell>
                      {copy.plural(
                        "analytics.dashboardBuilder.users",
                        cohort.size,
                      )}
                    </TableCell>
                    {intervals.map((day) => {
                      const point = getRetentionPoint(cohort, day);
                      return (
                        <TableCell key={`${cohort.cohort}-${day}`}>
                          <span
                            className="font-medium text-foreground"
                            data-analytics-cohort-retention-rate
                          >
                            {formatPercent(point?.retentionRate)}
                          </span>
                          <span
                            className="ml-2 text-xs text-muted-foreground"
                            data-analytics-cohort-retained-users
                          >
                            {point?.retainedUsers.toLocaleString() ?? "0"}
                          </span>
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      {copy.formatTime(cohort.lastActivity)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
